import React, {Fragment} from 'react'
import {SampleGraph} from 'common/SampleGraph'
import styles from './TRouteBottomPanel.scss'
import {PillGroup} from 'common/styleguide/PillGroup'
import {Set, OrderedMap} from 'immutable'
import {TrackLocation} from 'data/records'
import {featureTypes} from 'common/MapV2/featureTypes'
import _ from 'lodash'
import {PopoverLinkMenuItem} from 'common/styleguide/PopoverLinkMenu'
import {SelectionMenu} from './SelectionMenu'
import {showModal} from 'lib/modalUtils'
import {SAVE_AS_SEGMENT_MODAL} from 'common/modals/SaveAsSegmentModal'
import {SAVE_AS_NEW_ROUTE_MODAL} from 'common/modals/SaveAsNewRouteModal'
import {UPGRADE_MODAL} from 'features/recumbent/UpgradeModal'
import * as routeUtils from 'lib/routeUtils'
import * as trackpointUtils from 'lib/trackpointUtils'

const allMetrics = OrderedMap({
  ele: 'Ele',
  grade: 'Grade',
  hr: 'HR',
  cad: 'Cad',
  watts: 'Watts',
  speed: 'Speed',
  temp: 'Temp'
})

// only show ele and grade pills by default, until
// we can run the metrics calc
const defaultAvailableMetrics = allMetrics.filter(
  (v, k) => ['ele', 'grade'].includes(k)
)

export class TRouteBottomPanel extends React.PureComponent {
  state = {
    availableMetrics: defaultAvailableMetrics,
    activeMetrics: Set(['ele']),
    dirtyTRoute: null
  }

  componentDidMount () {
    // fish the available metrics out of the metrics calculator instance
    // for the sample graph, so we don't have to re-run the full metrics
    // calculation just to figure out what metrics to display
    const sg = this._sgComponent._sampleGraph
    const metrics = sg.metricsY()

    this.setState({
      availableMetrics: allMetrics.filter(
        (v, k) => metrics.find(m => m._key === k)
      )
    })
  }

  onActiveMetricsChange = (activeMetrics) => {
    if (activeMetrics.size === 0) { return } // don't allow the user to deselect all metrics
    this.setState({activeMetrics})
  }

  getSGHighlightSlice = () => {
    const {selectedFeature} = this.props
    if (!this.hasHighlightSlice()) {
      return undefined
    }
    // for now, the sample graph can't handle TrackLocation synthetic
    // points, so we convert to integers
    return selectedFeature.data.highlightSlice.map(TrackLocation.closestIndex)
  }

  getSGHoverSlice = () => {
    const {hoveredFeature} = this.props
    if (!this.hasHoverSlice()) {
      return undefined
    }
    // for now, the sample graph can't handle TrackLocation synthetic
    // points, so we convert to integers
    return hoveredFeature.data.highlightSlice.map(TrackLocation.closestIndex)
  }

  getSGHighlightPoint = () => {
    const {hoveredFeature} = this.props
    if (!_.get(hoveredFeature, 'data.highlightPoint')) {
      return undefined
    }
    // make a dummy {index} obj to match the older highlightPoint format
    // from RSPv2
    return {
      index: hoveredFeature.data.highlightPoint.index
    }
  }

  onSGPointHighlighted = (p) => {
    const {troute, onFeatureHovered} = this.props
    if (p === null) { // SG calls this with null sometimes to clear the point
      onFeatureHovered(null)
      return
    }
    const trackPoint = troute.trackPoints[p.index]
    onFeatureHovered({
      type: featureTypes.POINT_GENERIC,
      data: {highlightPoint: {trackPoint, index: p.index}}
    })
  }

  hasHighlightSlice = () => {
    const {selectedFeature} = this.props
    return selectedFeature && selectedFeature.data && selectedFeature.data.highlightSlice
  }

  hasHoverSlice = () => {
    const {hoveredFeature} = this.props
    return hoveredFeature && hoveredFeature.data && hoveredFeature.data.highlightSlice
  }

  // safe-access highlightSlice
  highlightSlice = () => {
    return this.hasHighlightSlice()
      ? this.props.selectedFeature.data.highlightSlice
      : null
  }

  highlightedPoints = (opts = {}) => {
    if (!this.highlightSlice()) {
      return []
    }
    const {troute} = this.props
    const indexes = this.highlightSlice().map(TrackLocation.closestIndex)
    if (opts.raw) {
      return troute.rawPoints.slice(indexes[0], indexes[1])
    }
    return troute.trackPoints.slice(indexes[0], indexes[1])
  }

  onSliceHighlighted = (segment) => {
    this.props.onFeatureSelected({
      type: featureTypes.SAMPLE_GRAPH_USER_SELECTION,
      data: {highlightSlice: segment}
    })
  }

  clearSelectedFeature = () => {
    this.props.onFeatureSelected(null)
  }

  applyRouteTransformationToSlice = (transformFn) => {
    const {troute, updateDirtyTRoute} = this.props
    const {dirtyTRoute} = this.state
    const r = dirtyTRoute || troute
    const indexes = this.highlightSlice().map(TrackLocation.closestIndex)
    const withTransform = transformFn(r, indexes[0], indexes[1])
    updateDirtyTRoute(withTransform)
    this.setState({
      dirtyTRoute: withTransform
    })
    this.clearSelectedFeature()
  }

  trimEnds = () => {
    if (this.props.currentUser.accountLevel === 0) { this.showEditUpsell(); return }
    this.applyRouteTransformationToSlice(
      trackpointUtils.trimEnds
    )
  }

  deleteSelection = () => {
    if (this.props.currentUser.accountLevel === 0) { this.showEditUpsell(); return }
    this.applyRouteTransformationToSlice(
      trackpointUtils.deleteSelection
    )
  }

  flattenElevation = () => {
    if (this.props.currentUser.accountLevel === 0) { this.showEditUpsell(); return }
    this.applyRouteTransformationToSlice(
      routeUtils.flattenElevation
    )
  }

  excludeFromMetrics = () => {
    if (this.props.currentUser.accountLevel === 0) { this.showEditUpsell(); return }
    this.applyRouteTransformationToSlice(
      routeUtils.excludeFromMetrics
    )
  }

  includeInMetrics = () => {
    this.applyRouteTransformationToSlice(
      routeUtils.includeInMetrics
    )
  }

  showEditUpsell = () => {
    showModal(UPGRADE_MODAL, {feature: 'rideCleanup'})
    this.clearSelectedFeature() // need this to clear SelectionMenu
  }

  revertDirtyTRoute = () => {
    this.props.revertDirtyTRoute()
    this.setState({dirtyTRoute: null})
  }

  saveDirtyTRoute = () => {
    const {updateTRoute, revertUnsavedChanges} = this.props
    const {dirtyTRoute} = this.state
    if (!dirtyTRoute) { return }

    updateTRoute(dirtyTRoute)
    this.setState({dirtyTRoute: null})
    revertUnsavedChanges()
  }

  render () {
    const {troute, objectType, currentUser, selectedFeature, metricUnits} = this.props
    const {dirtyTRoute, availableMetrics, activeMetrics} = this.state

    const hasHighlightSlice = this.hasHighlightSlice()
    const showExcludeFromMetrics = hasHighlightSlice && this.highlightedPoints({raw: true}).some(p => !p.k) // Some points in the highlighted portion are included in metrics
    const showIncludeInMetrics = hasHighlightSlice && this.highlightedPoints({raw: true}).some(p => !!p.k) // Some points in the highlighted portion have been excluded from metrics

    const editable = troute.permissions && troute.permissions.canBeModified && !troute.locked
    // basic data for "save as/edit" selection modals
    const modalData = this.hasHighlightSlice() && {troute, objectType, highlightSlice: selectedFeature.data.highlightSlice}
    const upsellEdits = currentUser.accountLevel === 0

    return (
      <div className={styles.TRouteBottomPanel}>
        <div className={styles.sgControls}>
          <PillGroup
            inline
            options={availableMetrics}
            selected={activeMetrics}
            onChange={this.onActiveMetricsChange} />
          <div className={styles.pullRight}>
            {dirtyTRoute && editable && <span className={styles.savePrompt}><a onClick={this.saveDirtyTRoute}>Save changes</a> or <a onClick={this.revertDirtyTRoute}>Revert</a>?</span>}
            {this.hasHighlightSlice() && <Fragment>
              {editable && <SelectionMenu disabled={upsellEdits} title='Edit'>
                <PopoverLinkMenuItem onClick={this.trimEnds}>Trim ends</PopoverLinkMenuItem>
                <PopoverLinkMenuItem onClick={this.deleteSelection}>Delete selection</PopoverLinkMenuItem>
                <PopoverLinkMenuItem onClick={this.flattenElevation}>Flatten selection</PopoverLinkMenuItem>
                {showExcludeFromMetrics && <PopoverLinkMenuItem onClick={this.excludeFromMetrics}>Exclude from metrics</PopoverLinkMenuItem>}
                {showIncludeInMetrics && <PopoverLinkMenuItem onClick={this.includeInMetrics}>Include in metrics</PopoverLinkMenuItem>}
              </SelectionMenu>}
              {currentUser.id && <SelectionMenu title='Save as'>
                <PopoverLinkMenuItem onClick={() => showModal(SAVE_AS_SEGMENT_MODAL, modalData)}>
                  Segment
                </PopoverLinkMenuItem>
                <PopoverLinkMenuItem onClick={() => showModal(SAVE_AS_NEW_ROUTE_MODAL, modalData)}>A new route</PopoverLinkMenuItem>
              </SelectionMenu>}
              <a className={styles.clearSelection} onClick={this.clearSelectedFeature}>
                Clear selection
              </a>
            </Fragment>}
            {!dirtyTRoute && !hasHighlightSlice && <span className={styles.hint}>Drag on graph to zoom/select</span>}
          </div>
        </div>
        {/* plotOnly is used to prevent the 25px height offset that sample graph assumes when it can't find an .sgBar */}
        <SampleGraph
          ref={r => { this._sgComponent = r }}
          plotOnly
          dragMode='select'
          segmentFormat='TrackLocation'
          height={145}
          rawPoints={(dirtyTRoute || troute).rawPoints}
          highlightPoint={this.getSGHighlightPoint()}
          highlightSlice={this.getSGHighlightSlice()}
          hoverSlice={this.getSGHoverSlice()}
          onSegmentSelected={this.onSliceHighlighted}
          onPointHighlighted={this.onSGPointHighlighted}
          metricsEnabled={activeMetrics}
          metricUnits={metricUnits} />
      </div>
    )
  }
}
